import { SxProps, Theme } from '@mui/material/styles';
import { ErrorBoundary } from '@sentry/react';

import React from 'react';

import { AiCallForCallPopDTO } from '_lib/api/apiTypes.shim';
import { Box } from '_lib/components/Box/Box';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';

import { useAppSelector } from '_lib/redux/hooks';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { toSxArray } from '_lib/utils/helpers';

import { CompanyContext, selectZoneZipContext } from './Company.slice';

import { CompanySelected } from './CompanySelected/CompanySelected';
import { ZipZoneSelectComponentType, ZipZoneSelectContext } from './CompanySelected/zipZoneSelectContext';

export type IframeContext = {
  callHandleIframeUrl: string | undefined;
  setIframeUrlPath: (url: 'login' | 'call handle') => void;
  iframePaneRef: React.RefObject<null>;
};

export type CompanyPaneProps = {
  phoneCallURLState: Pick<PhoneCallURLState, 'companyDNIS' | 'companyId'>;
  hideAllTabs?: boolean; // This is required because nested tabs are not 'rendered', causing an error
  sx?: SxProps<Theme>;
  companyContext: CompanyContext;
  handleSwitchCompany: (companyId: string) => Promise<void>;
  zipZoneOptions?: { ZipZoneSelectComponent: ZipZoneSelectComponentType };
  iframeContext?: IframeContext;
  aiCall?: AiCallForCallPopDTO['aiCall'] | null;
};

/**
 * NOTE - you can supply a custom ZipZoneSelect component, or just an externalZipOrPostal,
 * but NOT both! Reasoning is that a truly custom ZipZoneSelect component should also implement its own
 * external zip listening behavior
 */
export const CompanyPane = (props: CompanyPaneProps): React.ReactElement => {
  const {
    phoneCallURLState,
    sx,
    hideAllTabs,
    companyContext,
    handleSwitchCompany,
    zipZoneOptions,
    iframeContext,
    aiCall
  } = props;

  const zoneZipContext = useAppSelector(selectZoneZipContext);

  return (
    <ZipZoneSelectContext
      value={
        !!zipZoneOptions && 'ZipZoneSelectComponent' in zipZoneOptions ? zipZoneOptions.ZipZoneSelectComponent : null
      }>
      <Box sx={[{ display: 'flex', flexDirection: 'column' }, ...toSxArray(sx)]}>
        <ErrorBoundary
          beforeCapture={(scope) => {
            scope.setTag('pane', 'Company Pane');
          }}
          fallback={({ resetError }) => (
            <ErrorBoundaryFallback
              title='Error Displaying Company'
              onRefresh={() => {
                resetError();
              }}
            />
          )}>
          <CompanySelected
            onCompanySwitchConfirmed={handleSwitchCompany}
            companyContext={companyContext}
            zoneZipContext={zoneZipContext}
            phoneCallURLState={phoneCallURLState}
            hideAllTabs={hideAllTabs}
            iframeContext={iframeContext}
            aiCall={aiCall}
          />
        </ErrorBoundary>
      </Box>
    </ZipZoneSelectContext>
  );
};
